import { useCallback, useEffect, useState } from 'react';

const UP = 'ArrowUp';
const DOWN = 'ArrowDown';

// be sure to wrap onOpen/onClose in useCallback if they are declared in a function component body
// do not declare onOpen/onClose as an inline function in the hook call
export function useMenuTrigger<T extends HTMLElement | null>(
    isOpen: boolean,
    onOpen: () => void,
    onClose: () => void
): (node: T) => void {
    const [trigger, setTrigger] = useState<HTMLElement | null>(null);

    // handle key presses for menu trigger controls
    const handleKeyDown = useCallback(
        function (this: HTMLElement, e: KeyboardEvent): void {
            if (e.key === UP) {
                e.preventDefault();
                onOpen();
            } else if (e.key === DOWN) {
                e.preventDefault();
                onClose();
            }
        },
        [onOpen, onClose]
    );

    useEffect(() => {
        if (trigger) {
            trigger.setAttribute('aria-expanded', String(isOpen));
        }
    }, [isOpen, trigger]);

    useEffect(() => {
        if (trigger) {
            if (!trigger.hasAttribute('aria-haspopup')) {
                trigger.setAttribute('aria-haspopup', 'true');
            }
            trigger.addEventListener('keydown', handleKeyDown);
        }
        return () => {
            if (trigger) {
                trigger.removeEventListener('keydown', handleKeyDown);
            }
        };
    }, [trigger, handleKeyDown]);

    // assign to consuming component's ref prop
    const ref = useCallback(<T extends HTMLElement | null>(node: T) => {
        if (node) {
            setTrigger(node);
        }
    }, []);
    return ref;
}
